"use client";

import React from "react";
import { User, Calendar, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";


declare const wvcClient: any;

function SinglePostSkeleton() {
  return (
    <div className="animate-pulse max-w-4xl mx-auto">
      <div className="h-12 md:h-16 bg-muted rounded-md w-3/4 mx-auto mb-6"></div>
      <div className="flex justify-center items-center gap-4 mb-10">
        <div className="size-10 bg-muted rounded-lg"></div>
        <div className="h-5 bg-muted rounded-md w-32"></div>
      </div>
      <div className="h-[300px] md:h-[500px] bg-muted rounded-md mb-12 w-full"></div>
      <div className="space-y-4">
        <div className="h-4 bg-muted rounded-md w-full"></div>
        <div className="h-4 bg-muted rounded-md w-full"></div>
        <div className="h-4 bg-muted rounded-md w-5/6"></div>
        <div className="h-4 bg-muted rounded-md w-4/6"></div>
      </div>
    </div>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} data-index={i} className="h-72 bg-muted animate-pulse rounded-md"></div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.map((post, i) => (
        <div key={post.id} data-index={i} className="h-full">
          <Card className="flex flex-col h-full overflow-hidden border-border shadow-md hover:shadow-lg transition-shadow duration-300 bg-card rounded-md">
            {post.featuredImage && (
              <Link to={post.link || "#"} className="block h-48 overflow-hidden shrink-0">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform hover:scale-105 duration-700"
                />
              </Link>
            )}
            <CardHeader className="flex-1 pb-4">
              <Link to={post.link || "#"} className="hover:text-secondary transition-colors">
                <CardTitle className="text-xl font-default font-bold line-clamp-2 leading-tight text-card-foreground">
                  {post.title}
                </CardTitle>
              </Link>
            </CardHeader>
            <CardFooter className="pt-0 text-sm text-muted-foreground flex justify-between items-center">
              {post.date && (
                <span className="flex items-center gap-1.5 font-sans">
                  <Calendar className="size-3.5 text-secondary" />
                  {post.date}
                </span>
              )}
              <Link to={post.link || "#"} className="text-secondary font-bold hover:underline uppercase tracking-widest text-xs">
                Listen Now
              </Link>
            </CardFooter>
          </Card>
        </div>
      ))}
    </div>
  );
}

function RelatedPostsSection() {
  
  const related_wp_query_params = { post_type: "post", per_page: 3 };
  const related_wp_query = new WP_Query(related_wp_query_params);

  return (
    <div className="mt-16 md:mt-24">
      <h3 className="text-2xl md:text-3xl font-default font-bold mb-8 text-foreground uppercase tracking-tighter">
        More from the Studio
      </h3>
      <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return <SinglePostSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-default font-bold text-destructive mb-4">Signal Lost</h2>
        <p className="text-muted-foreground font-sans">We encountered an issue while loading this track. Please refresh the feed.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-default font-bold mb-4">Track Not Found</h2>
        <p className="text-muted-foreground font-sans">The composition you are looking for has been archived or moved.</p>
        <Link to="/HomePage" className="inline-block mt-6 text-secondary hover:underline font-bold uppercase tracking-widest text-sm">
          Back to Portfolio
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      <header className="mb-10 md:mb-14 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {post.categories.map((cat, i) => (
              <span
                key={cat.id}
                data-index={i}
                className="inline-flex items-center px-3 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-bold uppercase tracking-widest"
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl lg:text-7xl font-default font-bold text-foreground leading-none mb-8 tracking-tighter">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground text-sm font-medium font-sans">
          {post.author && (
            <div className="flex items-center gap-3">
              <Avatar className="size-10 border border-border rounded-md">
                {post.author.avatar && post.author.avatar["48"] ? (
                  <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
                ) : (
                  <AvatarFallback className="bg-muted text-secondary">
                    <User className="size-5" />
                  </AvatarFallback>
                )}
              </Avatar>
              <span className="text-foreground font-bold uppercase tracking-wide">{post.author.name}</span>
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4 text-secondary" />
              <time dateTime={post.date} className="uppercase tracking-wide">{post.date}</time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <figure className="mb-12 md:mb-16 rounded-md overflow-hidden shadow-2xl border border-border">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto max-h-[700px] object-cover grayscale hover:grayscale-0 transition-all duration-1000"
          />
        </figure>
      )}

      <div 
        className="prose prose-lg md:prose-xl dark:prose-invert max-w-none font-sans text-foreground/90 leading-relaxed [&>p]:mb-6 [&>p]:last:mb-0 [&>h2]:text-3xl [&>h2]:font-default [&>h2]:font-bold [&>h2]:mb-6 [&>h2]:mt-12 [&>h2]:text-foreground [&>h2]:uppercase [&>h2]:tracking-tight [&>h3]:text-2xl [&>h3]:font-default [&>h3]:font-bold [&>h3]:mb-4 [&>h3]:mt-8 [&>h3]:text-foreground [&>h4]:text-xl [&>h4]:font-default [&>h4]:font-bold [&>h4]:mb-4 [&>h4]:mt-6 [&>h4]:text-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2 [&>blockquote]:border-l-4 [&>blockquote]:border-secondary [&>blockquote]:pl-6 [&>blockquote]:py-4 [&>blockquote]:my-8 [&>blockquote]:italic [&>blockquote]:text-foreground [&>blockquote]:bg-muted/50 [&>blockquote]:rounded-md [&>blockquote]:font-serif [&>img]:rounded-md [&>img]:my-10 [&>img]:shadow-xl [&>a]:text-secondary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-secondary/80 [&>pre]:bg-muted [&>pre]:p-6 [&>pre]:rounded-md [&>pre]:overflow-x-auto [&>pre]:my-6 [&>pre]:border [&>pre]:border-border [&>code]:bg-muted [&>code]:px-1.5 [&>code]:py-0.5 [&>code]:rounded-md [&>code]:text-sm [&>code]:font-mono [&>code]:text-secondary"
        dangerouslySetInnerHTML={{ __html: post.content || '' }}
      />

      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Tag className="size-5 text-secondary" />
          {post.tags.map((tag, i) => (
            <Link
              key={tag.id}
              to={tag.link || "#"}
              data-index={i}
              className="px-3 py-1.5 bg-muted hover:bg-secondary hover:text-secondary-foreground text-muted-foreground rounded-md text-xs font-bold uppercase tracking-widest transition-all"
            >
              {tag.name}
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12 md:my-16 bg-border" />

      <RelatedPostsSection />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && typeof wvcClient !== 'undefined' 
    ? wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post" }; 
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="221915" id="single-post-template-section" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}